import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, X, MessageSquare, Loader2, Sparkles, ChevronRight, Bot } from 'lucide-react';
import { maxSalesChat } from '../services/maxSalesChat';

import { useTranslation } from 'react-i18next';
const ChatWidget = () => {
    const { t } = useTranslation();
const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const sendGreeting = useCallback(async () => {
    setIsTyping(true);
    try {
      const response = await maxSalesChat.sendMessage('');
      setMessages([
        {
          id: '1',
          sender: 'bot',
          content: response.content,
          timestamp: new Date(),
          actionButtons: response.actionButtons || [],
        },
      ]);
    } catch (error) {
      setMessages([
        {
          id: '1',
          sender: 'bot',
          content: 'Hello! I\'m the MaxEmployee sales assistant. What is your biggest business challenge today?',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  }, []);

  // Send greeting when chat opens
  useEffect(() => {
    if (open && messages.length === 0) {
      sendGreeting();
    }
  }, [open, messages.length, sendGreeting]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      sender: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await maxSalesChat.sendMessage(input);

      const botMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'bot',
        content: response.content,
        timestamp: new Date(),
        actionButtons: response.actionButtons || [],
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        sender: 'bot',
        content: 'Sorry, I\'m having connection issues. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleActionClick = (href) => {
    setOpen(false);
    // Navigate to the href — use window.location for now, or router.push if available
    if (href.startsWith('/marketplace') || href.startsWith('/skills') || href.startsWith('/contact')) {
      window.location.href = href;
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end">
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-4 py-3 rounded-full shadow-xl transition-all hover:scale-105 active:scale-95 group"
        >
          <div className="bg-black rounded-full p-1">
            <MessageSquare className="w-5 h-5 text-amber-500" />
          </div>
          <span className="font-bold text-sm uppercase tracking-wider">{t('chat_widget.find_your_ideal_agent')}</span>
          <div className="absolute right-full mr-4 bg-zinc-900 text-white text-[10px] px-2 py-1 rounded border border-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Find your ideal agent
          </div>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="w-[380px] h-[500px] bg-zinc-950 border border-zinc-800 rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-zinc-900 px-4 py-3 flex items-center justify-between border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <div className="bg-amber-500 p-1 rounded-md">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm">Max Sales — Sales Assistant</h3>
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest">{t('chat_widget.intelligent_and_personalized')}</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[85%]">
                  <div className={`p-3 rounded-2xl text-sm ${
                    msg.sender === 'user'
                      ? 'bg-amber-500 text-black rounded-tr-none'
                      : 'bg-zinc-800 text-zinc-200 rounded-tl-none border border-zinc-700'
                  }`}>
                    {/* Parse markdown-style bold */}
                    {msg.content.split('\n').map((line, i) => (
                      <div key={i}>
                        {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={j} className="font-bold text-white">{part.slice(2, -2)}</strong>;
                          }
                          return <span key={j}>{part}</span>;
                        })}
                        {line !== '' && <br />}
                      </div>
                    ))}
                  </div>

                  {/* Action buttons */}
                  {msg.actionButtons && msg.actionButtons.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {msg.actionButtons.map((btn, i) => (
                        <button
                          key={i}
                          onClick={() => handleActionClick(btn.href)}
                          className="flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-black text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <span>{btn.label}</span>
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 text-zinc-200 p-3 rounded-2xl rounded-tl-none border border-zinc-700 text-xs italic animate-pulse">
                  <Bot className="w-3 h-3 inline mr-1" />
                  Max is thinking...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-zinc-900 border-t border-zinc-800">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSend();
                }}
                placeholder={t('chat_widget.describe_your_business_challenge')}
                className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors placeholder-zinc-600"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black p-2 rounded-xl transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
